/**
 * Firebase error handling utility
 * Converts Firebase error codes to user-friendly messages
 */

export const getFirebaseErrorMessage = (error) => {
    if (!error || !error.code) {
        return error?.message || 'An unexpected error occurred. Please try again.';
    }

    switch (error.code) {
        // Authentication errors
        case 'auth/invalid-credential':
        case 'auth/wrong-password':
            return 'The email or password you entered is incorrect. Please check your credentials and try again.';

        case 'auth/user-not-found':
            return 'No account found with this email address. Please check the email or create a new account.';

        case 'auth/email-already-in-use':
            return 'An account with this email address already exists. Please sign in instead or use a different email.';

        case 'auth/weak-password':
            return 'Password is too weak. Please choose a stronger password with at least 6 characters.';

        case 'auth/invalid-email':
            return 'Please enter a valid email address.';

        case 'auth/user-disabled':
            return 'This account has been disabled. Please contact support for assistance.';

        case 'auth/too-many-requests':
            return 'Too many failed attempts. Please wait a few minutes before trying again.';

        case 'auth/network-request-failed':
            return 'Network error. Please check your internet connection and try again.';

        case 'auth/requires-recent-login':
            return 'For security reasons, please sign out and sign in again to complete this action.';

        case 'auth/invalid-verification-code':
            return 'The verification code is invalid. Please check and try again.';

        case 'auth/invalid-verification-id':
            return 'The verification ID is invalid. Please try again.';

        case 'auth/missing-verification-code':
            return 'Please enter the verification code.';

        case 'auth/missing-verification-id':
            return 'Verification ID is missing. Please try again.';

        case 'auth/popup-closed-by-user':
            return 'Sign-in was cancelled. Please try again.';

        case 'auth/popup-blocked':
            return 'Pop-up was blocked by your browser. Please allow pop-ups and try again.';

        case 'auth/cancelled-popup-request':
            return 'Sign-in was cancelled. Please try again.';

        case 'auth/unauthorized-domain':
            return 'This domain is not authorized for authentication. Please contact support.';

        case 'auth/operation-not-allowed':
            return 'This sign-in method is not enabled. Please contact support.';

        case 'auth/account-exists-with-different-credential':
            return 'An account already exists with the same email but different sign-in credentials. Please sign in using your original method.';

        case 'auth/credential-already-in-use':
            return 'This credential is already associated with a different account.';

        case 'auth/invalid-action-code':
            return 'The action code is invalid. This can happen if the code is malformed, expired, or has already been used.';

        case 'auth/expired-action-code':
            return 'The action code has expired. Please request a new one.';

        case 'auth/missing-email':
            return 'Please enter your email address.';

        case 'auth/missing-password':
            return 'Please enter your password.';

        case 'auth/internal-error':
            return 'An internal error occurred. Please try again later.';

        default:
            // Return the original message for unknown errors, but make it more user-friendly
            return error.message || 'An unexpected error occurred. Please try again or contact support if the problem persists.';
    }
};

export const isTemporaryError = (error) => {
    if (!error || !error.code) return false;

    const temporaryErrors = [
        'auth/network-request-failed',
        'auth/too-many-requests',
        'auth/internal-error'
    ];

    return temporaryErrors.includes(error.code);
};